import React, { useEffect, useState } from "react";
import { useGetUserScoreQuery } from "../../services/tracking.service";
import { CircularProgress, Grid, useTheme } from "@mui/material";
import { VTTypography } from "@virtus-tech-repository/virtus-tech-repository";
import ScoresContainer from "./ScoresContainer";
import { useAppSelector } from "../../store/hooks";
import { useGetMarksQuery } from "../../services/marks.service";
import {
    MarkedHotspot,
    QuestionHotspot,
    ScenesMarks,
} from "@virtus-tech-repository/virtus-tech-repository/lib/models/marks.model";

interface IMarksUsersScore {
    drawWidth: number;
    scenarioId: string;
    userId: string;
    attemptId: string;
}

export default function MarksUsersScore({ drawWidth, scenarioId, userId, attemptId }: IMarksUsersScore) {
    const { selectedVersion, selectedAttempt } = useAppSelector((state) => state.currentReducer);
    const theme = useTheme();
    const { data: marksData, refetch: refetchMarks } = useGetMarksQuery(
        selectedVersion ? selectedVersion : scenarioId!,
        {
            // pollingInterval: 5000, // get marks from server every 5secs
        },
    );

    const [weightedHotspots, setWeightedHotspots] = useState<{ id: string; weighting: number }[]>([]);
    const [totalMarks, setTotalMarks] = useState<number>(0);

    const [userScore, setUserScore] = useState<number | undefined>(undefined);
    const [sceneList, setSceneList] = useState<{ sceneId: string; correct: boolean }[]>([]);
    const [answeredStopPoints, setAnsweredStopPoints] = useState<
        { sceneId: string; questionId: string; correct: boolean }[]
    >([]);

    useEffect(() => {
        let tempWeightedHotspots: { id: string; weighting: number }[] = [];
        let tempTotalMarks: number = 0;
        marksData?.forEach((element: ScenesMarks) => {
            element.sceneHotspots.forEach((subElement: any) => {
                if (subElement.hotspotType === "question") {
                    subElement.answerHotspots.forEach((microElement: any) => {
                        if (microElement.marked) {
                            tempWeightedHotspots.push({
                                id: microElement.hotspotId,
                                weighting: microElement.weighting,
                            });
                            tempTotalMarks = tempTotalMarks + microElement.weighting;
                        }
                    });
                } else if (subElement.marked) {
                    tempWeightedHotspots.push({ id: subElement.hotspotId, weighting: subElement.weighting });
                    tempTotalMarks = tempTotalMarks + subElement.weighting;
                }
            });
        });
        setTotalMarks(tempTotalMarks);
        setWeightedHotspots(tempWeightedHotspots);
    }, [marksData]);

    useEffect(() => {
        if (selectedAttempt && weightedHotspots.length > 0) {
            let tempUserScore: number = 0;
            let TempSceneList: { sceneId: string; correct: boolean }[] = [];

            selectedAttempt.data.actions.forEach((element: any) => {
                let tempFirstAnswer: { sceneId: string; correct: boolean } | undefined = undefined;
                let tempFirstStopPointAnswer: { sceneId: string; questionId: string; correct: boolean }[] = [];

                element.hotspotActions.forEach((subElement: any) => {
                    if (subElement.action === "Clicked" && subElement.questionId) {
                        const tempFirstStopPointAnswerIncluded = tempFirstStopPointAnswer.find(
                            (findElement: { questionId: string; correct: boolean }) =>
                                findElement.questionId === subElement.questionId,
                        );
                        if (tempFirstStopPointAnswerIncluded === undefined) {
                            let actionWeighting = weightedHotspots.find(
                                (findElement: { id: string; weighting: number }) =>
                                    findElement.id === subElement.hotspotId,
                            );
                            if (actionWeighting !== undefined) {
                                tempFirstStopPointAnswer.push({
                                    sceneId: element.sceneId,
                                    questionId: subElement.questionId,
                                    correct: actionWeighting.weighting > 0,
                                });
                                tempUserScore = tempUserScore + actionWeighting.weighting;
                            }
                        }
                    } else if (subElement.action === "Clicked" && !subElement.questionId && !tempFirstAnswer) {
                        let actionWeighting = weightedHotspots.find(
                            (findElement: { id: string; weighting: number }) => findElement.id === subElement.hotspotId,
                        );
                        if (actionWeighting !== undefined) {
                            tempFirstAnswer = { sceneId: element.sceneId, correct: actionWeighting.weighting > 0 };
                            tempUserScore = tempUserScore + actionWeighting.weighting;
                        }
                    }
                });

                if (tempFirstAnswer) {
                    TempSceneList.push(tempFirstAnswer);
                }
                if (tempFirstStopPointAnswer.length > 0 && !tempFirstAnswer) {
                    setAnsweredStopPoints([...answeredStopPoints, ...tempFirstStopPointAnswer]);
                    TempSceneList.push({ sceneId: element.sceneId, correct: true });
                }
            });

            setUserScore(tempUserScore);
            setSceneList(TempSceneList);
        }
    }, [weightedHotspots, selectedAttempt]);

    return (
        <>
            <Grid
                item
                container
                sx={{
                    position: "absolute",
                    left: "20px",
                    top: "20px",
                    zIndex: 10000,
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: "15px",
                    width: "220px",
                    height: "180px",
                }}
            >
                {userScore === undefined ? (
                    <CircularProgress size={"6em"} sx={{ marginTop: "200px", marginLeft: "45%" }} />
                ) : (
                    <Grid container sx={{ width: "100%", heigth: "100%", position: "relative" }}>
                        <VTTypography sx={{ position: "absolute", left: "10px", top: "10px" }}>
                            Users Score
                        </VTTypography>

                        <>
                            <VTTypography type="Heading2" sx={{ position: "absolute", left: "60px", top: "60px" }}>
                                {userScore}
                            </VTTypography>
                            <VTTypography type="Heading1" sx={{ position: "absolute", left: "100px", top: "70px" }}>
                                /
                            </VTTypography>
                            <VTTypography type="Heading2" sx={{ position: "absolute", left: "130px", top: "100px" }}>
                                {totalMarks}
                            </VTTypography>
                        </>
                    </Grid>
                )}
            </Grid>
            {userScore === undefined ? (
                <CircularProgress size={"6em"} sx={{ marginTop: "200px", marginLeft: "45%" }} />
            ) : (
                <>
                    <ScoresContainer
                        drawWidth={drawWidth}
                        viewMarks
                        correctSceneList={sceneList}
                        answeredStopPoints={answeredStopPoints}
                    />
                </>
            )}
        </>
    );
}
